import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Fade,
  Grow,
  InputAdornment,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Circles } from "react-loader-spinner";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditNoteIcon from "@mui/icons-material/EditNote";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import GavelIcon from "@mui/icons-material/Gavel";
import Header from "../../../components/Header";
import { tokens } from "../../../theme";
import { AssetFinanceUrl } from "../../../config";
import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import useSubRightPermissionsHook from "../../../data/RBAC/SubRightPermissionHook";
import { BRAND_BLUE, BRAND_BLUE_LIGHT, lienColumns, exportToCsv } from "../stageBoardShared";
import RegisterLienDialog from "./RegisterLienDialog";
import LogbookViewerDialog from "./LogbookViewerDialog.jsx";
import MenuBookIcon from "@mui/icons-material/MenuBook";

const base_url = AssetFinanceUrl.uri;

const STAGES = [
  {
    key: "PENDING_REGISTRATION",
    title: "Lien Pending Registration",
    description: "Assets awaiting lien filing with the registry",
    icon: EditNoteIcon,
    accent: "warning",
    objectName: "lienRegistrationsByColumnValues",
    inputObjectName: "lien",
    parameters: { lienStatus: "PENDING", requireLien: true },
  },
  {
    key: "REGISTERED",
    title: "Lien Registered",
    description: "Liens successfully filed and active with the registry",
    icon: AssignmentTurnedInIcon,
    accent: "success",
    objectName: "lienRegistrationsByColumnValues",
    inputObjectName: "lien",
    parameters: { lienStatus: "REGISTERED", requireLien: true },
  },
];

const LienManagement = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { hasSubRightPermission } = useSubRightPermissionsHook();

  const [activeIndex, setActiveIndex] = useState(0);
  const [dataByStage, setDataByStage] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [registerRow, setRegisterRow] = useState(null);
  const [logbookRow, setLogbookRow] = useState(null);

  const stage = STAGES[activeIndex];

  const canRegisterLien = hasSubRightPermission("Lien Pending Registration", "EditPermission");
  const canExportStage = hasSubRightPermission(stage.title, "ExportPermission");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const responses = await Promise.all(
        STAGES.map((s) => fetchJsonDynamic(s.objectName, s.inputObjectName, s.parameters, base_url)),
      );
      const next = {};
      responses.forEach((res, idx) => {
        const key = STAGES[idx].key;
        let rows = [];
        if (res && !res.error && res.results) {
          try {
            const parsed = JSON.parse(res.results);
            rows = Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            rows = [];
          }
        }
        next[key] = { rows, error: res?.error || null };
      });
      setDataByStage(next);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const columnsForActiveStage = useMemo(() => {
    if (stage.key === "PENDING_REGISTRATION") {
      return [
        ...lienColumns,
        {
          field: "actions",
          headerName: "Actions",
          width: 180,
          sortable: false,
          filterable: false,
          renderCell: (params) =>
            canRegisterLien ? (
              <Button
                size="small"
                variant="contained"
                startIcon={<GavelIcon fontSize="small" />}
                onClick={() => setRegisterRow(params.row)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "6px",
                  background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${BRAND_BLUE_LIGHT} 100%)`,
                  "&:hover": {
                    boxShadow: "0 4px 10px rgba(23,102,162,0.35)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.18s ease",
                }}
              >
                Register Lien
              </Button>
            ) : (
              <Chip label="No access" size="small" variant="outlined" />
            ),
        },
      ];
    }
    if (stage.key === "REGISTERED") {
      return [
        ...lienColumns,
        {
          field: "logbook",
          headerName: "Logbook",
          width: 160,
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <Button
              size="small"
              variant="outlined"
              startIcon={<MenuBookIcon fontSize="small" />}
              onClick={() => setLogbookRow(params.row)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "6px",
                borderColor: BRAND_BLUE,
                color: BRAND_BLUE,
                "&:hover": {
                  bgcolor: `${BRAND_BLUE}12`,
                  borderColor: BRAND_BLUE_LIGHT,
                  transform: "translateY(-1px)",
                  boxShadow: "0 3px 8px rgba(23,102,162,0.2)",
                },
                transition: "all 0.18s ease",
              }}
            >
              View Logbook
            </Button>
          ),
        },
      ];
    }
    return lienColumns;
  }, [stage.key, canRegisterLien]);

  const filteredRows = useMemo(() => {
    const rows = dataByStage[stage.key]?.rows || [];
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some((v) => v !== null && v !== undefined && String(v).toLowerCase().includes(q)),
    );
  }, [dataByStage, stage.key, search]);

  const handleExport = () => {
    exportToCsv(filteredRows, columnsForActiveStage, `${stage.key.toLowerCase()}.csv`);
  };

  return (
    <Box m="20px">
      <Header
        title="Lien Management"
        subtitle="Track collateral liens from filing through registration"
      />

      {/* Process stepper */}
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          gap: 1,
          mb: 3,
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {STAGES.map((s, idx) => {
          const StageIcon = s.icon;
          const isActive = idx === activeIndex;
          const count = dataByStage[s.key]?.rows.length ?? 0;
          return (
            <React.Fragment key={s.key}>
              <Grow in timeout={280 + idx * 120}>
                <Paper
                  onClick={() => setActiveIndex(idx)}
                  elevation={isActive ? 6 : 1}
                  sx={{
                    flex: 1,
                    minWidth: 210,
                    cursor: "pointer",
                    p: 2,
                    borderRadius: "10px",
                    position: "relative",
                    overflow: "hidden",
                    border: isActive
                      ? `2px solid ${BRAND_BLUE}`
                      : `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e4eaf3"}`,
                    background: isActive
                      ? `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${BRAND_BLUE_LIGHT} 100%)`
                      : isDark
                        ? "#162736"
                        : "#ffffff",
                    color: isActive ? "#fff" : undefined,
                    transition: "all 0.22s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: isActive
                        ? "0 8px 20px rgba(23,102,162,0.4)"
                        : "0 6px 16px rgba(23,102,162,0.15)",
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "9px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isActive ? "rgba(255,255,255,0.2)" : `${colors.blueAccent[isDark ? 700 : 900]}`,
                        color: isActive ? "#fff" : BRAND_BLUE,
                      }}
                    >
                      <StageIcon fontSize="small" />
                    </Box>
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: isActive ? "rgba(255,255,255,0.22)" : undefined,
                        color: isActive ? "#fff" : undefined,
                      }}
                      color={isActive ? undefined : s.accent}
                    />
                  </Stack>
                  <Typography variant="subtitle2" fontWeight={700} mt={1.5}>
                    {s.title}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: isActive ? 0.85 : 0.65, display: "block", mt: 0.3 }}>
                    {s.description}
                  </Typography>

                  {s.key === "PENDING_REGISTRATION" && count > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: isActive ? "#fff" : "#f59e0b",
                        animation: "pulse 1.6s ease-in-out infinite",
                        "@keyframes pulse": {
                          "0%": { boxShadow: "0 0 0 0 rgba(245,158,11,0.55)" },
                          "70%": { boxShadow: "0 0 0 8px rgba(245,158,11,0)" },
                          "100%": { boxShadow: "0 0 0 0 rgba(245,158,11,0)" },
                        },
                      }}
                    />
                  )}
                </Paper>
              </Grow>
              {idx < STAGES.length - 1 && (
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    color: colors.grey[500],
                  }}
                >
                  <ChevronRightIcon />
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {/* Stage table */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e4eaf3"}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            p: 2,
            background: isDark
              ? "linear-gradient(135deg, #122033 0%, #1a3a54 100%)"
              : `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${BRAND_BLUE_LIGHT} 100%)`,
            color: "#fff",
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {stage.title}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {stage.description} • {filteredRows.length} record{filteredRows.length === 1 ? "" : "s"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "rgba(255,255,255,0.7)" }} fontSize="small" />
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: "rgba(255,255,255,0.14)",
                  borderRadius: "8px",
                  color: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "& input::placeholder": { color: "rgba(255,255,255,0.7)", opacity: 1 },
                },
              }}
              sx={{ minWidth: 200 }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={fetchAll} sx={{ color: "#fff" }}>
                <RefreshIcon
                  sx={{
                    animation: loading ? "spin 0.9s linear infinite" : "none",
                    "@keyframes spin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
            {canExportStage && (
              <Tooltip title="Export CSV">
                <IconButton onClick={handleExport} sx={{ color: "#fff" }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Box sx={{ position: "relative", minHeight: 360 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 360 }}>
              <Circles height="60" width="60" color={BRAND_BLUE} ariaLabel="loading" />
            </Box>
          ) : (
            <Fade in timeout={400} key={stage.key}>
              <Box>
                <DataGrid
                  autoHeight
                  rows={filteredRows}
                  columns={columnsForActiveStage}
                  getRowId={(row) => row.LienId ?? Math.random()}
                  disableSelectionOnClick
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  components={{
                    NoRowsOverlay: () => (
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 220 }}>
                        <Typography color="text.secondary">No records at this stage</Typography>
                      </Box>
                    ),
                  }}
                  sx={{
                    border: "none",
                    fontSize: "0.8rem",
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: isDark ? "#122033" : "#1d3a5c",
                      color: "#fff",
                      borderBottom: "none",
                      fontSize: "0.71rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                    },
                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton": {
                      color: "rgba(255,255,255,0.75)",
                    },
                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                      backgroundColor: isDark ? "rgba(255,255,255,0.025)" : "#f4f7fb",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: isDark ? "rgba(23,102,162,0.18)" : "#e8f0fe",
                      transition: "background-color 0.14s ease",
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(23,102,162,0.15)"}`,
                    },
                  }}
                />
              </Box>
            </Fade>
          )}
        </Box>
      </Paper>

      {registerRow && (
        <RegisterLienDialog
          open={Boolean(registerRow)}
          row={registerRow}
          base_url={base_url}
          onClose={() => setRegisterRow(null)}
          onSuccess={fetchAll}
        />
      )}

      {logbookRow && (
        <LogbookViewerDialog
          open={Boolean(logbookRow)}
          row={logbookRow}
          base_url={base_url}
          onClose={() => setLogbookRow(null)}
        />
      )}
    </Box>
  );
};

export default LienManagement;
